import { imgUser } from '@/assets/images';
import { Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import type { HomeStep } from '@/features/guardian/components/welcome-banner/types';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import { fontFamilies } from '@/theme/typography';
import React, { memo, useCallback } from 'react';
import { Image, Pressable, StyleSheet } from 'react-native';

// ─── Types ────────────────────────────────────────────────────────────────────

type GetStartedSectionProps = {
  currentStep: HomeStep;
  playerName?: string;
  playerAvatarUrl?: string;
  onStepPress?: (step: HomeStep) => void;
};

type StepItemData = {
  step: HomeStep;
  title: string;
  subtitle: string;
};

// ─── Step data ────────────────────────────────────────────────────────────────

const STEPS: StepItemData[] = [
  {
    step: 1,
    title: STRINGS.home.guardian.getStarted.steps.step1.title,
    subtitle: STRINGS.home.guardian.getStarted.steps.step1.subtitle,
  },
  {
    step: 2,
    title: STRINGS.home.guardian.getStarted.steps.step2.title,
    subtitle: STRINGS.home.guardian.getStarted.steps.step2.subtitle,
  },
  {
    step: 3,
    title: STRINGS.home.guardian.getStarted.steps.step3.title,
    subtitle: STRINGS.home.guardian.getStarted.steps.step3.subtitle,
  },
];

// ─── Step badge (number or checkmark) ─────────────────────────────────────────

type StepBadgeProps = {
  step: HomeStep;
  isDone: boolean;
  isActive: boolean;
};

const StepBadge = memo(({ step, isDone, isActive }: StepBadgeProps) => {
  const { scale } = useScaling();
  const size = scale(28);
  const isFilled = isActive || isDone;

  return (
    <Stack
      width={size}
      height={size}
      borderRadius={scale(6)}
      align="center"
      justify="center"
      backgroundColor={isFilled ? colors.Blue : guardianColors['3']}
      style={!isFilled ? stepBadgeStyle.locked : undefined}>
      <Text
        fontSize={scale(14)}
        fontWeight="700"
        fontFamily={fontFamilies.inter}
        style={isFilled ? stepBadgeStyle.textFilled : stepBadgeStyle.textLocked}>
        {step}
      </Text>
    </Stack>
  );
});

const stepBadgeStyle = StyleSheet.create({
  locked: { opacity: 0.6 },
  textFilled: { color: colors['0'] },
  textLocked: { color: guardianColors['6'] },
});

StepBadge.displayName = 'GetStarted.StepBadge';

// ─── Single step row ──────────────────────────────────────────────────────────

type StepRowProps = {
  data: StepItemData;
  currentStep: HomeStep;
  playerName?: string;
  playerAvatarUrl?: string;
  onPress?: (step: HomeStep) => void;
};

const StepRow = memo(
  ({ data, currentStep, playerName, playerAvatarUrl, onPress }: StepRowProps) => {
    const { scale } = useScaling();
    const styles = useStyles();

    const isDone = data.step < currentStep;
    const isLocked = currentStep === 1 && data.step > 1;
    const isActive = !isDone && !isLocked;

    const handlePress = useCallback(() => {
      if (!isLocked) {
        onPress?.(data.step);
      }
    }, [isLocked, onPress, data.step]);

    return (
      <Stack>
        <Pressable onPress={handlePress} disabled={isLocked}>
          {({ pressed }) => (
            <Stack
              direction="row"
              align="flex-start"
              gap={scale(12)}
              paddingY={scale(12)}
              style={pressed && styles.rowPressed}>
              <StepBadge step={data.step} isDone={isDone} isActive={isActive} />

              <Stack flex={1}>
                <Text
                  variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
                  style={[styles.stepTitle, isLocked && styles.stepTitleLocked]}
                  numberOfLines={1}>
                  {data.title}
                </Text>

                {isDone && data.step === 1 ? (
                  <Stack direction="row" align="center" gap={scale(6)} marginTop={scale(4)}>
                    <Image
                      source={playerAvatarUrl ? { uri: playerAvatarUrl } : imgUser}
                      style={styles.playerAvatar}
                    />
                    <Text
                      variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
                      style={styles.playerName}>
                      {playerName}
                    </Text>
                  </Stack>
                ) : (
                  <Text
                    variant="Font Guardian App/Subtitle/Subtitle 11 Regular"
                    style={[styles.stepSubtitle, isLocked && styles.stepSubtitleLocked]}
                    numberOfLines={1}>
                    {data.subtitle}
                  </Text>
                )}
              </Stack>

              {isDone && (
                <SvgIcon name="IcCheckCircleOutline" size={scale(20)} color={guardianColors.Blue} />
              )}
              {isActive && <SvgIcon name="IcChevronRight" size={scale(18)} color={colors['10']} />}
            </Stack>
          )}
        </Pressable>
      </Stack>
    );
  },
);

StepRow.displayName = 'GetStarted.StepRow';

// ─── Root component ───────────────────────────────────────────────────────────

function GetStartedSection({
  currentStep,
  playerName,
  playerAvatarUrl,
  onStepPress,
}: GetStartedSectionProps) {
  const styles = useStyles();

  return (
    <Stack style={styles.card}>
      <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.sectionTitle}>
        {STRINGS.home.guardian.getStarted.title}
      </Text>

      {STEPS.map(item => (
        <StepRow
          key={item.step}
          data={item}
          currentStep={currentStep}
          playerName={playerName}
          playerAvatarUrl={playerAvatarUrl}
          onPress={onStepPress}
        />
      ))}
    </Stack>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = createStyles(({ scale }) => ({
  card: {
    backgroundColor: colors['0'],
    borderRadius: scale(16),
    borderCurve: 'continuous',
    paddingHorizontal: scale(16),
    paddingTop: scale(16),
    paddingBottom: scale(4),
    shadowColor: '#D5D9E2',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 2,
  },
  sectionTitle: {
    color: colors['10'],
    marginBottom: scale(4),
  },
  stepTitle: {
    color: guardianColors['10'],
  },
  stepTitleLocked: {
    color: guardianColors['6'],
  },
  stepSubtitle: {
    color: guardianColors['6'],
    marginTop: scale(2),
  },
  stepSubtitleLocked: {
    color: guardianColors['5'],
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: guardianColors['3'],
  },
  rowPressed: {
    opacity: 0.7,
  },
  playerAvatar: {
    width: scale(24),
    height: scale(24),
    borderRadius: scale(12),
  },
  playerName: {
    color: colors['6'],
  },
}));

export default memo(GetStartedSection);
export type { GetStartedSectionProps };
